import { Address, ClientAddressType, LegalEntity } from '../../../common/types/client';
import { AppRoutes } from '../../../common/constants';
import { BaseClientType, EditClientStepProps } from '../../types';
import { Card, CardContent, Tab, Tabs } from '@mui/material';
import { DEFAULT_ERROR_MESSAGE, DEFAULT_SAVED_MESSAGE } from '../../../common/lib/fulcrumApi';
import { JSX, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useUpdateLegalEntity } from '../../../common/components/mutations';
import AddressDetailsStep from './steps/addressDetailsStep';
import BasicInformationStep from './steps/basicInformationStep';
import ClientPageHeaderSummary from '../../components/clientPageHeaderSummary';
import ClientRelatedPartiesStep from '../../components/editClientSteps/clientRelatedPartiesStep';
import clsx from 'clsx';
import Content from '../../../common/components/content';
import Layout from '../../../common/components/layout';
import LayoutLoadingState from '../../../common/components/layout/layoutLoadingState';
import StepFooter from '../../../common/components/stepFooter';
import ValuationStep from './steps/valuationStep';

const LAST_STEP_INDEX = 3;

/**
 * Legal entity edit page
 */
const EditLegalEntityPage = (): JSX.Element => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [activeTab, setActiveTab] = useState(0);
  const { mutateAsync, isPending: mutationIsLoading } = useUpdateLegalEntity();
  const { data: client } = useQuery<LegalEntity>({
    queryKey: [`/Juristic/${id}`],
  });

  const {
    control,
    reset,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<LegalEntity>({
    defaultValues: client,
    mode: 'onChange',
  });

  // Watch roleplayerID field to understand when form default values is populated (we don't show the form before that)
  const watchRoleplayerID = watch('roleplayerID');

  const commitChanges = async (): Promise<void> => {
    await handleSubmit(onSubmit)();
  };

  const onSubmit = async (data: LegalEntity): Promise<void> => {
    // Check if at least one valuation property is set to understand what value should be send(null or real values)
    const isValuationSet =
      data.valuation?.methodID !== null ||
      data.valuation?.value !== null ||
      data.valuation?.basisID !== null;

    try {
      await mutateAsync({
        ...data,
        valuation: isValuationSet ? data.valuation : null,
      });
      enqueueSnackbar(DEFAULT_SAVED_MESSAGE, { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (error as any).response?.data?.detail ||
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (error as any).response?.data?.title ||
          (error as Error).message ||
          DEFAULT_ERROR_MESSAGE,
        { variant: 'error' },
      );
      throw error;
    }
  };

  const handleSaveAndExit = (): void => {
    commitChanges().then(() => {
      navigate(`${AppRoutes.LegalEntities}/${id}`);
    });
  };

  // If there are more steps, go to the next one (otherwise save and go to the client page)
  const handleClickNext = async (): Promise<void> => {
    await commitChanges();
    if (Object.values(errors).length > 0) return;
    if (activeTab < LAST_STEP_INDEX) {
      setActiveTab((value) => value + 1);
    } else {
      handleSaveAndExit();
    }
  };

  // Reset form when client object changes
  useEffect(() => {
    if (client) {
      const formValues = { ...client };
      // add initial array fields if array is empty (otherwise the form will empty)
      // Add missing addresses items if it's not present
      if (!formValues.addresses?.find(({ typeID }) => typeID === ClientAddressType.business)) {
        formValues.addresses?.push({
          id: 0,
          city: '',
          country: '',
          code: '',
          province: '',
          street: '',
          typeID: ClientAddressType.business,
          typeNote: 'Business address',
          roleplayerID: client.roleplayerID,
        } as Address);
      }
      // If there are no contacts, add an empty one
      if (!formValues.contacts || formValues.contacts.length === 0) {
        formValues.contacts = [
          {
            email: '',
            firstname: '',
            phone: '',
            surname: '',
            id: 0,
            roleplayerID: client.roleplayerID,
          },
        ];
      }
      reset(formValues);
    }
  }, [client, reset]);

  if (!client || !watchRoleplayerID) {
    return <LayoutLoadingState />;
  }

  const breadcrumbs = [
    { label: 'Your Clients', href: AppRoutes.Clients },
    { label: client.roleplayerName },
  ];

  const stepProps = {
    watch,
    client,
    control,
    setValue,
    commitChanges,
  };

  return (
    <Layout breadcrumbs={breadcrumbs}>
      <Content>
        <ClientPageHeaderSummary client={client} />

        <Tabs
          className="mt-12"
          variant="scrollable"
          scrollButtons="auto"
          value={activeTab}
          onChange={(_, newValue: number): void => setActiveTab(newValue)}
        >
          {/* Disable tabs to prevent jumping between them and allow navigation only by clicking Next button */}
          <Tab disabled label="Basic information" />
          <Tab disabled label="Contact and address details" />
          <Tab disabled label="Valuation" />
          <Tab disabled label="Related parties" />
        </Tabs>
        <Card>
          <CardContent className={clsx(mutationIsLoading && 'animate-pulse')}>
            {activeTab === 0 && <BasicInformationStep {...stepProps} />}
            {activeTab === 1 && <AddressDetailsStep {...stepProps} />}
            {activeTab === 2 && <ValuationStep {...stepProps} />}
            {activeTab === 3 && (
              <ClientRelatedPartiesStep {...(stepProps as EditClientStepProps<BaseClientType>)} />
            )}
          </CardContent>
        </Card>
        <StepFooter
          isLoading={mutationIsLoading}
          onClickBack={activeTab > 0 ? (): void => setActiveTab((value) => value - 1) : undefined}
          onClickNext={handleClickNext}
          nextButtonTitle={activeTab === LAST_STEP_INDEX ? 'Save and close' : 'Next'}
        />
      </Content>
    </Layout>
  );
};

export default EditLegalEntityPage;
